/**
 * Timesheet Help Content
 *
 * Covers daily time entry, weekly view, effort reports, and team daily reports.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN TIMESHEET HELP
// =============================================================================

export const timesheetHelp: HelpContent = createHelpContent({
  id: 'timesheet',
  title: 'Timesheet',
  description: 'Log and track your daily work hours across projects and tasks',
  lastUpdated: '2026-02-08',
  version: '2.0',

  tips: [
    createTip(
      'tip-deadline',
      'warning',
      'Weekly Submission',
      'Timesheets should be submitted at the end of each week. Late submissions may delay project reporting and approvals.'
    ),
    createTip(
      'tip-allocation',
      'pro-tip',
      'Log Daily',
      'Logging time daily is more accurate than trying to remember at the end of the week. It takes just a minute each day.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Timesheet helps you log time spent on projects and tasks. Accurate time tracking supports:</p>
      <ul>
        <li><strong>Project billing</strong> — Ensure client projects are accurately billed</li>
        <li><strong>Resource planning</strong> — Help managers understand team capacity</li>
        <li><strong>Effort analysis</strong> — Compare planned vs actual effort</li>
        <li><strong>Personal insights</strong> — See how you spend your work time</li>
      </ul>`
    ),
    createSection(
      'daily-entry',
      'Daily Time Entry',
      `<p>To log time for a day:</p>
      <ol>
        <li>Select the date (defaults to today)</li>
        <li>Choose the project from your assigned projects</li>
        <li>Select the specific task (optional)</li>
        <li>Enter the hours worked</li>
        <li>Add a description of work performed (optional but recommended)</li>
        <li>Save the entry</li>
      </ol>
      <p>You can add multiple entries per day for different projects.</p>`
    ),
    createSection(
      'weekly-view',
      'Weekly View',
      `<p>The weekly view shows all your time entries for the current week at a glance:</p>
      <ul>
        <li><strong>Daily totals</strong> — Hours logged each day</li>
        <li><strong>Weekly total</strong> — Sum of all hours for the week</li>
        <li><strong>Project breakdown</strong> — Time per project</li>
        <li><strong>Edit entries</strong> — Click any cell to modify</li>
        <li><strong>Copy from previous week</strong> — Quickly replicate a similar week</li>
      </ul>`
    ),
    createSection(
      'submissions',
      'Submissions & Approvals',
      `<p>After logging your hours for the week:</p>
      <ol>
        <li>Review your entries for accuracy</li>
        <li>Click "Submit" to send the timesheet for approval</li>
        <li>Your manager will review and approve</li>
        <li>Once approved, entries are locked from editing</li>
      </ol>
      <p>If corrections are needed after submission, contact your manager to unlock the timesheet.</p>`
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-daily',
      'How to Log Your Daily Time',
      'Record your work hours for the day.',
      [
        {
          title: 'Open the Timesheet page',
          description: 'Navigate to the Timesheet from the sidebar menu.',
        },
        {
          title: 'Select today\'s date',
          description: 'The date defaults to today. Click on a different date if logging for a past day.',
        },
        {
          title: 'Choose a project',
          description: 'Select the project you worked on from the dropdown. Only your assigned projects appear.',
        },
        {
          title: 'Enter hours',
          description: 'Type the number of hours worked (e.g., 4, 2.5). You can use decimals for partial hours.',
        },
        {
          title: 'Save',
          description: 'Click Save. Repeat for other projects you worked on today.',
        },
      ],
      1
    ),
    createWalkthrough(
      'walkthrough-submit',
      'How to Submit Your Weekly Timesheet',
      'Submit your completed timesheet for approval.',
      [
        {
          title: 'Switch to weekly view',
          description: 'Use the weekly view to see all entries at once.',
        },
        {
          title: 'Review all entries',
          description: 'Check that each day has accurate hours and the correct projects.',
        },
        {
          title: 'Submit for approval',
          description: 'Click the "Submit" button. Your manager will be notified to review and approve.',
        },
      ],
      1
    ),
  ],

  troubleshooting: [
    createTroubleshooting(
      'trouble-not-saving',
      'Time entries not saving',
      [
        'Network connectivity issues',
        'Session timeout',
        'Invalid hours format',
      ],
      [
        {
          description: 'Check your connection',
          steps: [
            'Ensure you have a stable internet connection',
            'Try refreshing the page and re-entering the data',
          ],
        },
        {
          description: 'Verify input format',
          steps: [
            'Hours should be entered as numbers (e.g., 4, 2.5)',
            'Maximum daily hours is typically 24',
            'Negative hours are not allowed',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-approval-delay',
      'Timesheet approval taking too long',
      [
        'Manager has not reviewed yet',
        'Manager is on leave',
      ],
      [
        {
          description: 'Follow up',
          steps: [
            'Send a reminder to your manager',
            'If manager is unavailable, contact HR for alternate approver',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-mismatch',
      'Effort hours don\'t match expectations',
      [
        'Entries logged under wrong project',
        'Draft entries not submitted',
        'Different date range being compared',
      ],
      [
        {
          description: 'Review entries',
          steps: [
            'Check the weekly view for any entries under incorrect projects',
            'Verify all entries are saved (not just typed but not saved)',
            'Ensure you are comparing the same date ranges in reports',
          ],
        },
      ]
    ),
  ],

  faqs: [
    createFAQ(
      'faq-deadline',
      'What\'s the deadline for submitting my timesheet?',
      'Timesheets are typically due at the end of each week. Check with your manager for specific deadlines. Late submissions may affect project reporting.',
      ['deadline', 'submit', 'due', 'when']
    ),
    createFAQ(
      'faq-edit-after',
      'Can I edit time entries after submitting?',
      'Once submitted, entries are locked. Contact your manager to unlock the timesheet for corrections. Alternatively, corrections may need to be made in the next period.',
      ['edit', 'change', 'submitted', 'locked', 'correct']
    ),
    createFAQ(
      'faq-meetings',
      'How do I log time for meetings or admin work?',
      'Select the appropriate project or use the designated "Internal" or "Administrative" category for non-billable work like meetings, planning, and admin tasks.',
      ['meetings', 'admin', 'internal', 'non-billable']
    ),
    createFAQ(
      'faq-project-missing',
      'My project is not showing in the dropdown',
      'Only projects you are assigned to appear in the dropdown. Contact your project manager to be added to the project. If you were recently added, try refreshing the page.',
      ['missing', 'project', 'dropdown', 'not showing']
    ),
    createFAQ(
      'faq-overtime',
      'How do I log overtime?',
      'Log the actual hours worked, even if they exceed 8 hours in a day. Overtime tracking depends on your company policy. Your manager will see the extra hours in approvals.',
      ['overtime', 'extra', 'hours', 'more than 8']
    ),
    createFAQ(
      'faq-copy',
      'Can I copy entries from a previous week?',
      'Yes, use the "Copy Previous Week" feature in the weekly view. This copies the project and task structure but resets hours to zero so you can fill in the actuals.',
      ['copy', 'previous', 'repeat', 'duplicate']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Effort Report', '/effort-report', { description: 'View your effort analytics and breakdowns' }),
    createRelatedLink('Projects', '/projects', { description: 'View your assigned projects' }),
    createRelatedLink('Analytics', '/analytics', { description: 'Team and org effort analytics' }),
  ],

  keywords: [
    'timesheet',
    'hours',
    'time tracking',
    'effort',
    'billing',
    'work log',
    'submission',
    'approval',
    'daily',
    'weekly',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const timesheetEffortReportHelp: HelpContent = createHelpContent({
  id: 'timesheet:effort-report',
  title: 'Effort Report',
  description: 'View your personal effort metrics and project breakdowns',
  parentId: 'timesheet',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The Effort Report shows a breakdown of your logged hours by project, task, and time period. Use it to understand how your time is distributed and compare against allocations.</p>'
    ),
    createSection(
      'filters',
      'Using Filters',
      '<p>Filter the report by date range, project, or category to focus on specific aspects of your effort. Export to CSV for detailed analysis.</p>'
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'Why does my effort report not match my timesheet?', 'The effort report aggregates submitted entries. Draft entries that haven\'t been saved or submitted won\'t appear.', ['mismatch', 'different', 'wrong']),
    createFAQ('faq-2', 'Can I see effort for past periods?', 'Yes, use the date range filter to view any historical period.', ['past', 'history', 'old']),
  ],
  keywords: ['effort', 'report', 'breakdown', 'personal', 'hours'],
});

export const timesheetTeamDailyHelp: HelpContent = createHelpContent({
  id: 'timesheet:team-daily',
  title: 'Team Daily Report',
  description: 'Day-by-day view of team activities and time logged',
  parentId: 'timesheet',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The Team Daily Report shows each team member\'s logged hours for a selected date. Use this for daily standups, tracking team activity, and identifying members who haven\'t logged time.</p>'
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'Who can see the team daily report?', 'Team leads, managers, and admins can view the team daily report for their direct reports.', ['access', 'who', 'permission']),
  ],
  keywords: ['team', 'daily', 'report', 'standup', 'activity'],
  visibility: { visibleTo: ['manager', 'team_lead', 'admin'] },
});
